import classNames from 'classnames'
import { useState } from 'react'
import {
  Button,
  Icon,
  Tooltip,
  Typography,
  DropdownContent,
  Dropdown,
  type IDropdownItem,
  type IButtonProps,
} from 'shared/ui'
import { Select } from 'shared/ui/Select/Select'
import { SeatPlanIncludesTooltip } from 'pages/settings/ui/BillingPlan/ui/PlanIncludesTooltipContent/SeatPlanIncludesTooltip'
import { NumberPlanIncludesTooltip } from 'pages/settings/ui/BillingPlan/ui/PlanIncludesTooltipContent/NumberPlanIncludesTooltip'
import styles from './styles.module.scss'

export type IConnectWithSalesDropdown = {
  onChatWithSales: () => void
  onBookMeeting: () => void
  onShowGetCallModal: () => void
}

export type IPlan = {
  name: string
  icon: 'cube' | 'atom' | 'diamond'
  description: string
  selectItems: IDropdownItem[]
  onSelectValue: (value: string | number, item: IDropdownItem) => void
  buttonProps?: Partial<IButtonProps>
  connectWithSalesDropdown?: IConnectWithSalesDropdown
  iconColor: string
  showTooltip?: boolean
  price: string
  suffix: string | null
  showCustomCredits?: boolean
  isCustom?: boolean
}

type IPlanContainerProps = {
  plan: IPlan
  additionalNumberPrice: string
  additionalSeatPrice: string
  selectedValue: string | number
  onSelectValue: (value: string | number, item: IDropdownItem) => void
  className?: string
}

export const PlanContainer = ({
  plan,
  additionalNumberPrice,
  additionalSeatPrice,
  selectedValue,
  onSelectValue,
  className,
}: IPlanContainerProps) => {
  const [isActiveDropdown, setActiveDropdown] = useState(false)

  return (
    <div className={classNames(styles.planContainer, className)}>
      <Icon fontSize={32} icon={plan.icon} color={plan.iconColor} className={styles.planIcon} />
      <Typography ariaLabel='plans_header_title' variant='heading-sm' className={styles.planTitle}>
        {plan.name}
      </Typography>
      <Typography
        ariaLabel='plans_header_description'
        variant='body-md-regular'
        className={styles.planDescription}
      >
        {plan.description}
      </Typography>

      <div className={styles.infoContainer}>
        <div>
          <Typography ariaLabel='plans_header_title' variant='heading-lg'>
            {plan.price}
          </Typography>

          {plan.suffix && (
            <Typography
              className={styles.infoContainer__suffix}
              ariaLabel='plans_header_title'
              variant='body-md-regular'
            >
              {plan.suffix}
            </Typography>
          )}
        </div>

        {plan.showCustomCredits && (
          <Typography
            className={styles.customCredits}
            tag={'div'}
            ariaLabel='custom_credits'
            variant='text-input-field-r'
          >
            Custom amount of credits
          </Typography>
        )}
        {!plan.showCustomCredits && (
          <Select
            variant='stroke'
            items={plan.selectItems}
            value={selectedValue}
            dropdownProps={{
              withSearch: false,
            }}
            textFieldPlaceholderProps={{
              className: styles.selectPlan,
            }}
            onSelectValue={onSelectValue}
          />
        )}
        {plan.connectWithSalesDropdown ? (
          <Dropdown
            ariaLabel='ConnectWithSalesDropdown'
            show={isActiveDropdown}
            onChangeOpen={setActiveDropdown}
            placement='top'
            triggerComponent={() => (
              <Button
                text='Connect with sales'
                typeBtn='contained'
                size='large'
                fullWidth
                {...plan.buttonProps}
                iconRight={isActiveDropdown ? 'chevronUp' : 'chevronDown'}
                onClick={() => setActiveDropdown(!isActiveDropdown)}
              />
            )}
            customComponent={() => (
              <DropdownContent noPadding width={296}>
                <div className={styles.dropdownList}>
                  <Button
                    icon={'calendarPlus2'}
                    text='Book a meeting'
                    typeBtn='menuItem'
                    onClick={() => {
                      setActiveDropdown(false)
                      plan.connectWithSalesDropdown?.onBookMeeting()
                    }}
                  />
                  <Button
                    id='intercom-custom-plan_modal-button'
                    icon={'conversation'}
                    text='Start a chat'
                    typeBtn='menuItem'
                    onClick={() => {
                      setActiveDropdown(false)
                      plan.connectWithSalesDropdown?.onChatWithSales()
                    }}
                  />
                  <Button
                    icon={'phoneIncomingCall'}
                    text='Get a call'
                    typeBtn='menuItem'
                    onClick={() => {
                      setActiveDropdown(false)
                      plan.connectWithSalesDropdown?.onShowGetCallModal()
                    }}
                  />
                </div>
              </DropdownContent>
            )}
          />
        ) : (
          <Button text='Select plan' size='large' fullWidth {...plan.buttonProps} />
        )}
      </div>

      <div className={styles.includesData}>
        <Typography
          className={styles.includesDataTitle}
          ariaLabel='includes_data_title'
          variant='body-md-semibold'
        >
          Includes:
        </Typography>
        <div className={styles.includesDataLine}>
          <Icon icon='contacts' color={plan.iconColor} />
          <Typography
            className={styles.includesDataLineText}
            ariaLabel='includes_data_line_text'
            variant='body-md-regular'
          >
            {plan.isCustom ? 'Custom seats' : '1 seat'}
          </Typography>
          {plan.showTooltip && (
            <Tooltip
              type='description'
              label={<SeatPlanIncludesTooltip additionalSeatPrice={additionalSeatPrice} />}
              placement='top'
            >
              <Icon icon='lineInfoCircle' fontSize={12} color='var(--content-primary-tertiary)' />
            </Tooltip>
          )}
        </div>
        <div className={styles.includesDataLine}>
          <Icon icon='phone' color={plan.iconColor} />
          <Typography
            className={styles.includesDataLineText}
            ariaLabel='includes_data_line_text'
            variant='body-md-regular'
          >
            {plan.isCustom ? 'Custom numbers' : '1 number'}
          </Typography>
          {plan.showTooltip && (
            <Tooltip
              type='description'
              label={<NumberPlanIncludesTooltip additionalNumberPrice={additionalNumberPrice} />}
              placement='top'
            >
              <Icon icon='lineInfoCircle' fontSize={12} color='var(--content-primary-tertiary)' />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
